import { useRef, useState } from 'react';
import { pledge, acknowledgePledge, withdrawPledge } from '../services/shares';

/**
 * WishShareSection — the gifter-side UI for a shareable ("chip in") wish.
 * FEEDBACK_PLAN.md §6. Rendered by WishGiftCard only in the GIFTER branch, so
 * the wisher (owner) never sees it — matching the API, which strips the whole
 * contributions surface from the blind owner.
 *
 * It shows:
 *   - a running tally / progress bar computed from `contributions`
 *     ("3 of 4 shares claimed" and/or "$75 of $100 pledged"), counting only
 *     non-withdrawn rows;
 *   - a pledge control (a pieces stepper and/or an amount input) once a
 *     coordinator exists (someone has claimed the wish);
 *   - if the viewer IS the coordinator (`wish.claimed_by_user_id === user.id`),
 *     a pledge list with an "Acknowledge" button per still-pending pledge;
 *   - a "Withdraw" button on the viewer's own still-pending pledge.
 *
 * `notify(type, text)` and `onChanged()` come from the host page (WishesTab,
 * PersonDetail, PetDetail) — the same flash + refetch pattern used by claim /
 * purchase, so the tally refreshes after every action.
 */

function money(raw) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    return n === Math.round(n) ? `$${Math.round(n)}` : `$${n.toFixed(2)}`;
}

// One pledge, worded: "2 shares", "$25", or "2 shares · $25".
function describeContribution(c) {
    const parts = [];
    if (c.shares_pledged) parts.push(`${c.shares_pledged} share${Number(c.shares_pledged) === 1 ? '' : 's'}`);
    if (c.amount != null && Number(c.amount) > 0) parts.push(money(c.amount));
    return parts.join(' · ') || 'a pledge';
}

export default function WishShareSection({ wish, user, busy = false, notify = () => {}, onChanged = () => {} }) {
    const [pledging, setPledging] = useState(false);
    const [actingId, setActingId] = useState(null); // contribution id being ack'd/withdrawn
    const [shares, setShares] = useState(1);
    const [amount, setAmount] = useState('');
    const amountRef = useRef(null);

    // Round a preset split to a clean value: whole dollars, else 2dp.
    const presetAmount = (raw) => {
        const n = Math.round(Number(raw) * 100) / 100;
        return String(n === Math.round(n) ? Math.round(n) : n.toFixed(2));
    };

    const hasShares = wish.share_count != null;
    const hasPrice = wish.estimated_price != null;

    const contributions = wish.contributions || [];
    const active = contributions.filter((c) => c.status !== 'withdrawn');
    const acknowledged = active.filter((c) => c.status === 'acknowledged');

    const sum = (rows, field) => rows.reduce((t, c) => t + (Number(c[field]) || 0), 0);
    const pledgedShares = sum(active, 'shares_pledged');
    const pledgedAmount = sum(active, 'amount');
    const ackAmount = sum(acknowledged, 'amount');

    const coordinatorExists = !!wish.claimed_by_user_id;
    const iAmCoordinator = wish.claimed_by_user_id && wish.claimed_by_user_id === user?.id;
    const myPending = active.find((c) => c.user_id === user?.id && c.status === 'pledged');

    // The pieces stepper appears when the wish is divided into shares; the amount
    // input appears when there's a price target, partial funding is allowed, or
    // there's no share count to pledge against.
    const showStepper = hasShares;
    const showAmount = hasPrice || wish.allow_partial_funding || !hasShares;

    const busyAny = busy || pledging || actingId != null;

    const submitPledge = async () => {
        const payload = {};
        if (showStepper) payload.shares_pledged = Number(shares);
        if (showAmount && amount !== '') payload.amount = Number(amount);
        if (!payload.shares_pledged && !payload.amount) {
            notify('error', 'Enter a number of shares or an amount to pledge.');
            return;
        }
        setPledging(true);
        try {
            await pledge(wish.id, payload);
            notify('success', 'Your pledge was recorded.');
            setShares(1);
            setAmount('');
            await onChanged();
        } catch (err) {
            notify('error', err.response?.data?.error || 'Could not record your pledge.');
        } finally {
            setPledging(false);
        }
    };

    const ack = async (c) => {
        setActingId(c.id);
        try {
            await acknowledgePledge(c.id);
            notify('success', 'Pledge acknowledged.');
            await onChanged();
        } catch (err) {
            notify('error', err.response?.data?.error || 'Could not acknowledge the pledge.');
        } finally {
            setActingId(null);
        }
    };

    const withdraw = async (c) => {
        setActingId(c.id);
        try {
            await withdrawPledge(c.id);
            notify('success', 'Your pledge was withdrawn.');
            await onChanged();
        } catch (err) {
            notify('error', err.response?.data?.error || 'Could not withdraw your pledge.');
        } finally {
            setActingId(null);
        }
    };

    // The primary progress metric drives the bar: pieces if the wish is divided
    // into shares, otherwise the dollar target.
    const target = hasShares ? Number(wish.share_count) : hasPrice ? Number(wish.estimated_price) : 0;
    const filled = hasShares ? pledgedShares : pledgedAmount;
    const pct = target > 0 ? Math.min(100, Math.round((filled / target) * 100)) : 0;

    return (
        <div className="rounded-md border border-teal-100 bg-teal-50/50 p-3 space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-teal-700">Group gift</span>
                {coordinatorExists && (
                    <span className="text-xs text-gray-500">
                        Coordinated by {iAmCoordinator ? 'you' : wish.claimed_by?.name || 'someone'}
                    </span>
                )}
            </div>

            {/* Tally */}
            <div className="space-y-1">
                {hasShares && (
                    <p className="text-sm text-gray-700">
                        {pledgedShares} of {wish.share_count} shares claimed
                    </p>
                )}
                {hasPrice && (
                    <p className="text-sm text-gray-700">
                        {money(pledgedAmount) || '$0'} of {money(wish.estimated_price)} pledged
                        {ackAmount > 0 && (
                            <span className="text-gray-400"> · {money(ackAmount)} acknowledged</span>
                        )}
                    </p>
                )}
                {!hasShares && !hasPrice && (
                    <p className="text-sm text-gray-700">
                        {money(pledgedAmount) || '$0'} pledged so far
                    </p>
                )}
                {target > 0 && (
                    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full rounded-full bg-teal-500" style={{ width: `${pct}%` }} />
                    </div>
                )}
            </div>

            {/* Pledge control — needs a coordinator to exist first. */}
            {coordinatorExists ? (
                <div className="space-y-2">
                    {/* Quick-split presets — only when there's a price to split. */}
                    {showAmount && hasPrice && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-gray-500">Quick split:</span>
                            <button
                                type="button"
                                disabled={busyAny}
                                onClick={() => setAmount(presetAmount(Number(wish.estimated_price) / 2))}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                            >
                                Half
                            </button>
                            <button
                                type="button"
                                disabled={busyAny}
                                onClick={() => setAmount(presetAmount(Number(wish.estimated_price) / 3))}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                            >
                                Third
                            </button>
                            <button
                                type="button"
                                disabled={busyAny}
                                onClick={() => {
                                    setAmount('');
                                    amountRef.current?.focus();
                                }}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                            >
                                Custom
                            </button>
                        </div>
                    )}
                    <div className="flex flex-wrap items-end gap-2">
                    {showStepper && (
                        <label className="text-xs text-gray-600">
                            Shares
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={shares}
                                onChange={(e) => setShares(e.target.value)}
                                className="mt-0.5 block w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </label>
                    )}
                    {showAmount && (
                        <label className="text-xs text-gray-600">
                            Amount ($)
                            <input
                                ref={amountRef}
                                type="number"
                                min="0"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="mt-0.5 block w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </label>
                    )}
                    <button
                        type="button"
                        disabled={busyAny}
                        onClick={submitPledge}
                        className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {pledging ? 'Pledging…' : 'Pledge'}
                    </button>
                    </div>
                </div>
            ) : (
                <p className="text-xs text-gray-500">Claim this gift to coordinate before anyone can pledge.</p>
            )}

            {/* The viewer's own still-pending pledge can be withdrawn. */}
            {myPending && (
                <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>You pledged {describeContribution(myPending)} (awaiting acknowledgement)</span>
                    <button
                        type="button"
                        disabled={busyAny}
                        onClick={() => withdraw(myPending)}
                        className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Withdraw
                    </button>
                </div>
            )}

            {/* Coordinator's ack list. */}
            {iAmCoordinator && active.length > 0 && (
                <div className="pt-1 border-t border-teal-100 space-y-1">
                    <p className="text-xs font-medium text-gray-600">Pledges</p>
                    {active.map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-gray-700 truncate">
                                {c.user?.name || 'Someone'} — {describeContribution(c)}
                            </span>
                            {c.status === 'acknowledged' ? (
                                <span className="text-green-600 whitespace-nowrap">Acknowledged</span>
                            ) : (
                                <button
                                    type="button"
                                    disabled={busyAny}
                                    onClick={() => ack(c)}
                                    className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                                >
                                    Acknowledge
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
